import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
  Paper,
  Alert,
} from "@mui/material";
import Cropper from "react-easy-crop";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ImageCropper = ({
  onCropComplete: handleFinalCroppedImage,
  FIXED_WIDTH,
  FIXED_HEIGHT,
  MAX_FILESIZE,
  setSnackbarMessage,
  setOpenErrorSnackbar,
  setMatchingItem
}) => {
  const [image, setImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [displaytip, setDisplaytip] = useState(true);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
  
      if (file.size < MAX_FILESIZE) {
        const reader = new FileReader();
        reader.onload = () => {
          setImage(reader.result);
          setOpen(true);
  
          const fileName = file.name;
          setMatchingItem?.((prev) => ({
            ...prev,
            IMAGEPATH: fileName, 
          }));
        };
        reader.readAsDataURL(file);
      } else {
        setSnackbarMessage?.(
          "File size exceeds the " + MAX_FILESIZE / 1048576 + "MB limit."
        );
        setOpenErrorSnackbar?.(true);
        return;
      }
    }
  };
  

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = useCallback(async () => {
    try {
      const croppedImg = await getCroppedImg(
        image,
        croppedAreaPixels,
        rotation,
        FIXED_WIDTH,
        FIXED_HEIGHT
      );
      handleFinalCroppedImage(croppedImg);
      setOpen(false);
    } catch (e) {
      console.error("Error creating cropped image:", e);
    }
  }, [croppedAreaPixels, rotation, image, handleFinalCroppedImage]);

  const getCroppedImg = (
    imageSrc,
    pixelCrop,
    rotation = 0,
    targetWidth,
    targetHeight
  ) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = imageSrc;

      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d");

        tempCanvas.width = pixelCrop.width;
        tempCanvas.height = pixelCrop.height;

        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate((rotation * Math.PI) / 180);
        tempCtx.translate(-tempCanvas.width / 2, -tempCanvas.height / 2);

        tempCtx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );

        ctx.drawImage(
          tempCanvas,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          targetWidth,
          targetHeight
        );

        resolve(canvas.toDataURL("image/jpeg"));
      };
    });
  };

  const handleClose = () => setOpen(false);

  return (
    <Box mt={2}>
      <Button
        variant="contained"
        component="label"
        startIcon={<CloudUploadIcon />}
      >
        Upload Image
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Crop Image
          {displaytip && (
            <Alert severity="info" onClose={() => setDisplaytip(false)}>
              Tip: Fit the image in the box and adjust the zoom to crop the
              image. You can drag the image to reposition.
            </Alert>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: "relative", height: 400, marginBottom: 2 }}>
            {image && (
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={FIXED_WIDTH / FIXED_HEIGHT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <Typography gutterBottom>Zoom</Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, z) => setZoom(z)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={createCroppedImage}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Crop Image
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageCropper;
